import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Property, Auction } from '../../types';
import { useAppStore } from '../../store/appStore';
import { useState } from 'react';
import { ShareButtons } from './ShareButtons';
import { useToast } from '../common/Toast';

interface PropertyCardProps {
  property: Property | Auction;
  variant?: 'default' | 'compact' | 'horizontal';
}

export function PropertyCard({ property, variant = 'default' }: PropertyCardProps) {
  const { isFavorite, toggleFavorite, isAuthenticated } = useAppStore();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const isAuction = property.adKind === 'Aukcija';
  const auction = isAuction ? (property as Auction) : null;
  const favoriteStatus = isFavorite(property.id);

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('sr-RS').format(price) + ' ' + currency;
  };
  
  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.warning('Prijava potrebna', 'Morate biti ulogovani da biste dodali oglas u omiljene');
      return;
    }
    
    setIsLoading(true);
    try {
      await toggleFavorite(property.id);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Popularity badge based on views
  const getPopularityBadge = () => {
    if (property.views >= 50000) return { text: 'Viralno', color: 'bg-red-500' };
    if (property.views >= 10000) return { text: 'Ekstremno popularno', color: 'bg-pink-500' };
    if (property.views >= 5000) return { text: 'Vrlo popularno', color: 'bg-purple-600' };
    if (property.views >= 1000) return { text: 'Popularno', color: 'bg-purple-500' };
    return null;
  };
  const popularityBadge = getPopularityBadge();

  // Povoljno badge logic per ad type:
  // - Prodaja (non-plac): cena po m² < 1500€
  // - Izdavanje: mesečna renta < 200€ (ukupna cena, ne po m²)
  // - Plac (građevinsko zemljište, samo prodaja): cena po aru < 1000€
  const isPovoljno = (() => {
    if (property.adType === 'Izdavanje') {
      // Za izdavanje: mesečna cena ispod 200€
      return property.price > 0 && property.price < 200;
    }
    if (property.adType === 'Prodaja') {
      if (property.category === 'Plac') {
        // Samo za građevinsko zemljište na prodaju
        if (property.subcategory !== 'Građevinsko zemljište') return false;
        if (!property.squareMeters || property.squareMeters <= 0) return false;
        // Cena po aru (1 ar = 100m²): (price / m²) * 100
        const pricePerAr = (property.price / property.squareMeters) * 100;
        return pricePerAr < 1000;
      }
      // Ostale nekretnine na prodaju: cena po m² < 1500€
      if (!property.squareMeters || property.squareMeters <= 0) return false;
      return (property.price / property.squareMeters) < 1500;
    }
    return false;
  })();

  const getTimeRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    
    if (diff <= 0) return 'Završeno';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    return `${hours}h`;
  };

  const linkPath = isAuction
    ? `/aukcije/${property.id}`
    : property.adKind === 'Potražnja'
    ? `/potraznja/${property.id}`
    : `/ponuda/${property.id}`;

  const isDemand = property.adKind === 'Potražnja';
  const hasImages = property.images && property.images.length > 0 && property.images[0];

  // Za potražnje bez slika, prikaži kompaktan prikaz
  if (isDemand && !hasImages) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -4 }}
        className="bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden group relative"
      >
        <Link to={linkPath} className="block">
          <div className="p-5">
            {/* Header with badges */}
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex gap-2 flex-wrap">
                {property.isUrgent && (
                  <span className="px-2 py-1 text-xs font-bold rounded-full bg-red-500 text-white animate-pulse">
                    🔥 HITNO!
                  </span>
                )}
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                  property.adType === 'Prodaja' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' : 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400'
                }`}>
                  {property.adType === 'Prodaja' ? 'Kupovina' : 'Iznajmljivanje'}
                </span>
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                  Potražnja
                </span>
              </div>
              <div className="flex gap-2">
                <ShareButtons
                  url={linkPath}
                  title={property.title}
                  description={property.description}
                />
                {isAuthenticated && (
                  <button
                    onClick={handleToggleFavorite}
                    disabled={isLoading}
                    className={`p-2 rounded-lg text-sm font-medium transition-colors ${
                      favoriteStatus 
                        ? 'bg-red-100 text-red-600 dark:bg-red-900/20' 
                        : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
                    }`}
                    title={favoriteStatus ? 'Ukloni iz omiljenih' : 'Dodaj u omiljene'}
                  >
                    {favoriteStatus ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Title */}
            <h3 className="font-semibold text-slate-900 dark:text-white hover:text-amber-600 transition-colors line-clamp-2 mb-3">
              {property.title}
            </h3>

            {/* Location */}
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
              {property.location.city}{property.location.region ? `, ${property.location.region}` : ''}
            </p>

            {/* Details */}
            <div className="flex flex-wrap gap-3 text-sm text-slate-600 dark:text-slate-400 mb-4">
              {property.category && (
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  {property.category}
                </span>
              )}
              {/* Površina - za Potražnja prikazujemo raspon */}
              {isDemand && (property.squareMetersMin || property.squareMetersMax) ? (
                <span>{property.squareMetersMin || '0'} - {property.squareMetersMax || '∞'} m²</span>
              ) : property.squareMeters > 0 && (
                <span>{property.squareMeters} m²</span>
              )}
              {/* Sobe - za Potražnja prikazujemo raspon */}
              {isDemand && (property.roomsMin || property.roomsMax) ? (
                <span>{property.roomsMin || '0'} - {property.roomsMax || '∞'} soba</span>
              ) : Number(property.rooms) > 0 && (
                <span>{property.rooms} soba</span>
              )}
            </div>

            {/* Budget */}
            <div className="pt-3 border-t border-slate-100 dark:border-slate-700">
              <span className="text-sm text-slate-500 dark:text-slate-400">Budžet:</span>
              <span className="text-xl font-bold text-amber-600 ml-2">
                {(property.priceMin || property.priceMax) ? (
                  <>
                    {property.priceMin ? formatPrice(property.priceMin, property.currency) : '0'}
                    {' - '}
                    {property.priceMax ? formatPrice(property.priceMax, property.currency) : '∞'}
                  </>
                ) : (
                  formatPrice(property.price, property.currency)
                )}
              </span>
            </div>
          </div>
        </Link>
      </motion.div>
    );
  }

  if (variant === 'horizontal') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow flex flex-col sm:flex-row sm:h-[200px]"
      >
        <Link to={linkPath} className="sm:w-64 h-48 sm:h-full flex-shrink-0 relative">
          {hasImages ? (
            <img src={property.images[0]} alt={property.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full min-h-[12rem] bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
              <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
          {/* Badge on image */}
          <div className={`absolute top-3 ${isDemand ? 'left-3' : 'right-3'} px-3 py-1 rounded-full text-sm font-medium ${
            isDemand ? 'bg-purple-500 text-white' : property.adType === 'Prodaja' ? 'bg-emerald-500 text-white' : 'bg-blue-500 text-white'
          }`}>
            {isDemand ? 'Potražnja' : property.adType}
          </div>
        </Link>
        <div className="p-4 flex-1 flex flex-col overflow-hidden">
          {/* Title + Share */}
          <div className="flex items-start justify-between gap-2 mb-1">
            <Link to={linkPath}>
              <h3 className="font-semibold text-lg text-slate-900 dark:text-white hover:text-amber-600 transition-colors line-clamp-1">
                {property.title}
              </h3>
            </Link>
            <ShareButtons
              url={linkPath}
              title={property.title}
              description={property.description}
            />
          </div>
          
          {/* Location */}
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
            {property.location.city}{property.location.region ? `, ${property.location.region}` : ''}
          </p>
          
          {/* Description */}
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-2 line-clamp-2 hidden sm:block">
            {property.description}
          </p>
          
          {/* Price and stats section */}
          <div className="mt-auto flex flex-wrap items-center justify-between gap-4">
            <div className="flex gap-6">
              {/* Specs - Površina */}
              {isDemand && (property.squareMetersMin || property.squareMetersMax) ? (
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Površina</p>
                  <p className="text-slate-600 dark:text-slate-400">{property.squareMetersMin || '0'} - {property.squareMetersMax || '∞'} m²</p>
                </div>
              ) : property.squareMeters > 0 && (
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Površina</p>
                  <p className="text-slate-600 dark:text-slate-400">{property.squareMeters} m²</p>
                </div>
              )}
              {/* Specs - Sobe */}
              {isDemand && (property.roomsMin || property.roomsMax) ? (
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Sobe</p>
                  <p className="text-slate-600 dark:text-slate-400">{property.roomsMin || '0'} - {property.roomsMax || '∞'}</p>
                </div>
              ) : Number(property.rooms) > 0 && (
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Sobe</p>
                  <p className="text-slate-600 dark:text-slate-400">{property.rooms}</p>
                </div>
              )}
              {/* Price */}
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {isDemand ? 'Budžet' : 'Cena'}
                </p>
                <p className="text-xl font-bold text-amber-600">
                  {isDemand && (property.priceMin || property.priceMax) ? (
                    // Za Potražnja prikazujemo raspon cena
                    <>
                      {property.priceMin ? formatPrice(property.priceMin, property.currency) : '0'}
                      {' - '}
                      {property.priceMax ? formatPrice(property.priceMax, property.currency) : '∞'}
                    </>
                  ) : (
                    // Za Ponuda prikazujemo običnu cenu
                    <>
                      {formatPrice(property.price, property.currency)}
                      {property.adType === 'Izdavanje' && <span className="text-sm font-normal">/mes</span>}
                    </>
                  )}
                </p>
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="flex gap-2">
              {isAuthenticated && (
                <button
                  onClick={handleToggleFavorite}
                  disabled={isLoading}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    favoriteStatus 
                      ? 'bg-red-100 text-red-600 dark:bg-red-900/20' 
                      : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`}
                  title={favoriteStatus ? 'Ukloni iz omiljenih' : 'Dodaj u omiljene'}
                >
                  {favoriteStatus ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  )}
                </button>
              )}
              <Link
                to={linkPath}
                className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 transition-colors"
              >
                Pogledaj
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Default (grid) variant - matching AuctionCard structure
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow"
    >
      <Link to={linkPath}>
        <div className="relative">
          {hasImages ? (
            <img src={property.images[0]} alt={property.title} className="w-full h-48 object-cover" />
          ) : (
            <div className="w-full h-48 bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
              <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
          {/* Badge on image */}
          <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-sm font-medium ${
            isDemand ? 'bg-purple-500 text-white' : property.adType === 'Prodaja' ? 'bg-emerald-500 text-white' : 'bg-blue-500 text-white'
          }`}>
            {isDemand ? 'Potražnja' : property.adType}
          </div>
          {/* HITNO / Urgent badge */}
          {property.isUrgent && (
            <div className="absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-bold bg-red-500 text-white animate-pulse flex items-center gap-1">
              🔥 HITNO!
            </div>
          )}
          {/* Popularity badge - shows below urgent if both exist */}
          {popularityBadge && !property.isUrgent && (
            <div className={`absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium ${popularityBadge.color} text-white`}>
              {popularityBadge.text}
            </div>
          )}
          {popularityBadge && property.isUrgent && (
            <div className={`absolute top-12 left-3 px-2 py-1 rounded-full text-xs font-medium ${popularityBadge.color} text-white`}>
              {popularityBadge.text}
            </div>
          )}
        </div>
      </Link>
      <div className="p-4">
        {/* Title + Share */}
        <div className="flex items-center justify-between mb-2">
          <Link to={linkPath}>
            <h3 className="font-semibold text-slate-900 dark:text-white hover:text-amber-600 transition-colors">
              {property.title}
            </h3>
          </Link>
          <ShareButtons
            url={linkPath}
            title={property.title}
            description={property.description}
          />
        </div>
        
        {/* Location */}
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
          {property.location.city}{property.location.region ? `, ${property.location.region}` : ''}
        </p>
        
        {/* Specs row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-4 text-sm text-slate-600 dark:text-slate-400">
            {/* Površina - za Potražnja prikazujemo raspon */}
            {isDemand && (property.squareMetersMin || property.squareMetersMax) ? (
              <span>{property.squareMetersMin || '0'} - {property.squareMetersMax || '∞'} m²</span>
            ) : property.squareMeters > 0 && (
              <span>{property.squareMeters} m²</span>
            )}
            {/* Sobe - za Potražnja prikazujemo raspon */}
            {isDemand && (property.roomsMin || property.roomsMax) ? (
              <span>{property.roomsMin || '0'} - {property.roomsMax || '∞'} soba</span>
            ) : Number(property.rooms) > 0 && (
              <span>{property.rooms} soba</span>
            )}
            {property.category && <span>{property.category}</span>}
          </div>
        </div>
        
        {/* Price section */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {isDemand ? 'Budžet' : 'Cena'}
            </p>
            <p className="text-xl font-bold text-amber-600">
              {isDemand && (property.priceMin || property.priceMax) ? (
                // Za Potražnja prikazujemo raspon cena
                <>
                  {property.priceMin ? formatPrice(property.priceMin, property.currency) : '0'}
                  {' - '}
                  {property.priceMax ? formatPrice(property.priceMax, property.currency) : '∞'}
                </>
              ) : (
                // Za Ponuda prikazujemo običnu cenu
                <>
                  {formatPrice(property.price, property.currency)}
                  {property.adType === 'Izdavanje' && <span className="text-sm font-normal">/mes</span>}
                </>
              )}
            </p>
          </div>
          {/* Key features */}
          {(isPovoljno || property.features?.uknjizen) && (
            <div className="flex gap-1">
              {isPovoljno && (
                <span className="px-2 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded">Povoljno</span>
              )}
              {property.features?.uknjizen && (
                <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">Uknjižen</span>
              )}
            </div>
          )}
        </div>
        
        {/* Action buttons */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-700">
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {property.views} pregleda
          </span>
          <div className="flex gap-2">
            {isAuthenticated && (
              <button
                onClick={handleToggleFavorite}
                disabled={isLoading}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  favoriteStatus 
                    ? 'bg-red-100 text-red-600 dark:bg-red-900/20' 
                    : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
                }`}
                title={favoriteStatus ? 'Ukloni iz omiljenih' : 'Dodaj u omiljene'}
              >
                {favoriteStatus ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                )}
              </button>
            )}
            <Link
              to={linkPath}
              className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 transition-colors"
            >
              Pogledaj
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
